import { computed } from 'vue'
import {
  CANONICAL_HOSTS,
  CANONICAL_HOST_REGISTRY,
  HOST_TO_REGION,
  type RegionCode,
  type RegionHostEntry,
} from '@/config/hosts'

/**
 * Canonical hostname classifier. THE only place on the frontend that parses
 * `window.location.hostname`.
 *
 * SRP: this composable owns hostname parsing. Every consumer reads derived
 * values from here — nobody else should call `window.location.hostname` for
 * classification, region derivation, or canonical/WL detection.
 *
 * OCP: to add a new region host, edit `frontend/src/config/hosts.ts` — never
 * this file. All derived computeds pick up the new entry automatically.
 *
 * DIP: consumers depend on this composable's abstractions, not on the browser
 * global.
 *
 * SSR safe — every access guards `typeof window`. In a non-browser context
 * every computed resolves to a safe default (empty hostname → treated as
 * canonical, no region, not WL).
 */
export function useCurrentHost() {
  /** Bare, lowercased hostname (e.g. "zaveato.pk"). Empty string in SSR. */
  const hostname = computed<string>(() =>
    typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase()
  )

  /** True for localhost / 127.* / 192.168.* / *.local. */
  const isLocalDev = computed<boolean>(() => {
    const h = hostname.value
    if (!h) return false
    return (
      h === 'localhost' ||
      h.startsWith('127.') ||
      h.startsWith('192.168.') ||
      h.endsWith('.local')
    )
  })

  /**
   * True for any known Zaveato host (regional apex, staging sibling, dev
   * `.local`, loopback). Non-canonical = white-label custom domain.
   */
  const isCanonical = computed<boolean>(() => {
    const h = hostname.value
    if (!h) return true // SSR — assume canonical, don't render as WL
    return CANONICAL_HOSTS.has(h) || h.endsWith('.local')
  })

  /** Opposite of canonical. WL custom-branded storefront domain. */
  const isWhiteLabel = computed<boolean>(() => !isCanonical.value && !isLocalDev.value)

  /**
   * Which region this canonical host belongs to. `null` for WL custom
   * domains, unknown hosts, and (deliberately) loopback — a plain
   * `localhost` visit shouldn't be labelled as any particular region.
   */
  const region = computed<RegionCode | null>(() => HOST_TO_REGION.get(hostname.value) ?? null)

  /** Full registry entry for the current host, or `null` if unmapped. */
  const regionEntry = computed<RegionHostEntry | null>(() => {
    const code = region.value
    return code ? CANONICAL_HOST_REGISTRY[code] : null
  })

  return {
    hostname,
    isLocalDev,
    isCanonical,
    isWhiteLabel,
    region,
    regionEntry,
  }
}
